// src/components/MeetingList.jsx
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../../config';
import styles from './MeetingList.module.css';
import { FaCalendarAlt, FaSpinner, FaLink, FaPlayCircle, FaEdit, FaTrash, FaEllipsisV, FaSun, FaGlobe, FaVideo, FaVideoSlash, FaTimes, FaSave } from 'react-icons/fa';
import Sidebar from './Sidebar';

// This ZUID should come from your user authentication session, not hardcoded.
const CURRENT_USER_ZUID = "664033335";

const MeetingList = () => {
     const navigate = useNavigate();
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('upcoming');
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [recordings, setRecordings] = useState({}); // meetingKey -> { loading, hasRecording, playUrl }
    const [startingKey, setStartingKey] = useState(null); // meetingKey currently resolving its host start link
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [recordingModalMeeting, setRecordingModalMeeting] = useState(null); // meeting object, or null when closed
    const [recordingUrlInput, setRecordingUrlInput] = useState('');
    const [savingRecording, setSavingRecording] = useState(false);
    const [recordingModalError, setRecordingModalError] = useState('');

    const openRecordingModal = (meeting) => {
        setRecordingModalMeeting(meeting);
        setRecordingUrlInput(recordings[meeting.meetingKey]?.playUrl || '');
        setRecordingModalError('');
    };

    const closeRecordingModal = () => {
        setRecordingModalMeeting(null);
        setRecordingUrlInput('');
        setRecordingModalError('');
    };

    const handleSaveRecording = async () => {
        if (!recordingModalMeeting) return;
        if (!recordingUrlInput.trim()) {
            setRecordingModalError('Please paste the recording URL.');
            return;
        }
        setSavingRecording(true);
        setRecordingModalError('');
        try {
            const response = await fetch(API.SET_RECORDING_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    meetingKey: recordingModalMeeting.meetingKey,
                    recordingUrl: recordingUrlInput.trim(),
                }),
            });
            const result = await response.json();
            if (result.success) {
                setRecordings(prev => ({
                    ...prev,
                    [recordingModalMeeting.meetingKey]: { loading: false, hasRecording: true, playUrl: result.playUrl },
                }));
                closeRecordingModal();
            } else {
                setRecordingModalError(result.message || 'Failed to save recording URL.');
            }
        } catch {
            setRecordingModalError('Could not reach the server. Please try again.');
        } finally {
            setSavingRecording(false);
        }
    };

    const handleSyncRecordings = async () => {
        setSyncing(true);
        setSyncMessage('');
        try {
            const response = await fetch(API.SYNC_RECORDINGS_ENDPOINT);
            const result = await response.json();
            if (result.success) {
                setSyncMessage(`Checked ${result.checked} meeting${result.checked === 1 ? '' : 's'}, attached ${result.updated} new recording${result.updated === 1 ? '' : 's'}.`);
                setRecordings({}); // clear cache so items re-check and pick up anything newly attached
            } else {
                setSyncMessage(result.message || 'Sync failed.');
            }
        } catch {
            setSyncMessage('Sync failed — could not reach the server.');
        } finally {
            setSyncing(false);
        }
    };

    const fetchMeetings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API.GET_ZOHO_MEETINGS_ENDPOINT}?status=${statusFilter}`);
            if (!response.ok) {
                throw new Error('Failed to fetch meetings.');
            }
            const result = await response.json();
            if (result.success) {
                setMeetings(result.meetings);
            } else {
                setError(result.message || 'Failed to retrieve meetings.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeetings();
        setSelectMode(false);
        setSelectedKeys([]);
    }, [statusFilter]);

    // For past meetings, check whether a recording has been attached (cached locally
    // after the first check, so this only hits Zoho once per meeting).
    useEffect(() => {
        if (statusFilter !== 'past' || meetings.length === 0) return;

        meetings.forEach((meeting) => {
            const key = meeting.meetingKey;
            if (recordings[key]) return; // already checked or in flight

            setRecordings(prev => ({ ...prev, [key]: { loading: true } }));
            fetch(`${API.GET_RECORDING_ENDPOINT}?meetingKey=${key}`)
                .then(res => res.json())
                .then(result => {
                    setRecordings(prev => ({
                        ...prev,
                        [key]: result.success
                            ? { loading: false, hasRecording: !!result.hasRecording, playUrl: result.playUrl }
                            : { loading: false, hasRecording: false, error: true },
                    }));
                })
                .catch(() => {
                    setRecordings(prev => ({ ...prev, [key]: { loading: false, hasRecording: false, error: true } }));
                });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, meetings]);

    // Meetings the current user is allowed to delete (own upcoming meetings)
    const deletableMeetings = meetings.filter(
        m => statusFilter === 'upcoming' && m.presenterZuid === CURRENT_USER_ZUID
    );

    const toggleSelectMode = () => {
        setSelectMode(prev => !prev);
        setSelectedKeys([]);
    };

    const toggleSelectKey = (meetingKey) => {
        setSelectedKeys(prev =>
            prev.includes(meetingKey) ? prev.filter(k => k !== meetingKey) : [...prev, meetingKey]
        );
    };

    const toggleSelectAll = () => {
        if (selectedKeys.length === deletableMeetings.length) {
            setSelectedKeys([]);
        } else {
            setSelectedKeys(deletableMeetings.map(m => m.meetingKey));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedKeys.length === 0) return;
        const confirmDelete = window.confirm(
            `Are you sure you want to delete ${selectedKeys.length} meeting${selectedKeys.length > 1 ? 's' : ''}?`
        );
        if (!confirmDelete) return;

        setBulkDeleting(true);
        setError(null);
        try {
            const results = await Promise.all(
                selectedKeys.map(async (meetingKey) => {
                    try {
                        const response = await fetch(`${API.DELETE_ZOHO_MEETING_ENDPOINT}?meetingKey=${meetingKey}`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ meetingKey }),
                        });
                        const result = await response.json();
                        return result.success;
                    } catch {
                        return false;
                    }
                })
            );
            const failCount = results.filter(r => !r).length;
            if (failCount > 0) {
                setError(`${failCount} of ${selectedKeys.length} meetings could not be deleted.`);
            }
            setSelectedKeys([]);
            setSelectMode(false);
            fetchMeetings();
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleDelete = async (meetingKey) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this meeting?");
        if (!confirmDelete) return;

        try {
            setLoading(true);
            const response = await fetch(`${API.DELETE_ZOHO_MEETING_ENDPOINT}?meetingKey=${meetingKey}`, {
                method: 'DELETE',
                 headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ meetingKey: meetingKey }), // Send meetingKey in the body
        });
            

            if (!response.ok) {
                throw new Error('Failed to delete meeting.');
            }
            const result = await response.json();
            if (result.success) {
                fetchMeetings();
            } else {
                setError(result.message || 'Failed to delete meeting.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (meetingKey) => {
        try {
            setLoading(true);
            setError(null);
            
            // 1. Fetch the single meeting data from your backend API
            const response = await fetch(`${API.GET_SINGLE_MEETING_ENDPOINT}?meetingKey=${meetingKey}`);
            if (!response.ok) {
                throw new Error('Failed to fetch meeting details.');
            }
            const result = await response.json();
            
            if (result.success) {
                // 2. Navigate to the schedule page with the fetched data
                navigate('/edit-meeting', { state: { meetingToEdit: result.meeting } });
            } else {
                setError(result.message || 'Could not retrieve meeting data.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleDropdown = (meetingKey) => {
        setOpenDropdown(openDropdown === meetingKey ? null : meetingKey);
    };

    // The meetings list only gives us a joinLink (attendee link). To get host
    // controls (start recording, mute participants, etc.) the presenter must
    // open Zoho's startLink instead, which only the single-meeting API returns.
    const handleStartMeeting = async (meeting) => {
        setStartingKey(meeting.meetingKey);
        try {
            const response = await fetch(`${API.GET_SINGLE_MEETING_ENDPOINT}?meetingKey=${meeting.meetingKey}`);
            const result = await response.json();
            const startLink = result?.meeting?.startLink;
            window.open(startLink || meeting.joinLink, '_blank', 'noopener,noreferrer');
            if (!startLink) {
                setError('Could not get the host start link — opened the regular join link instead. Recording/host controls may be unavailable.');
            }
        } catch (err) {
            window.open(meeting.joinLink, '_blank', 'noopener,noreferrer');
            setError('Could not get the host start link — opened the regular join link instead.');
        } finally {
            setStartingKey(null);
        }
    };

      const handleJoinMeeting = (meetingKey) => {
        navigate(`/meeting/${meetingKey}`);
    };
    return (
        <div className={styles.withSidebar}>
            <Sidebar/>
        <div className={styles.container}>
           
            <motion.div
                className={styles.card}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h2 className={styles.title}>
                    <FaCalendarAlt /> Meeting Schedule
                </h2>
                <div className={styles.filterContainer}>
                    <button
                        onClick={() => setStatusFilter('upcoming')}
                        className={`${styles.filterButton} ${statusFilter === 'upcoming' ? styles.active : ''}`}
                    >
                        Upcoming
                    </button>
                    <button
                        onClick={() => setStatusFilter('past')}
                        className={`${styles.filterButton} ${statusFilter === 'past' ? styles.active : ''}`}
                    >
                        Past
                    </button>

                    {statusFilter === 'upcoming' && deletableMeetings.length > 0 && (
                        <button onClick={toggleSelectMode} style={selectToggleBtn}>
                            {selectMode ? 'Cancel' : 'Select'}
                        </button>
                    )}

                    {statusFilter === 'past' && (
                        <button onClick={handleSyncRecordings} disabled={syncing} style={selectToggleBtn}>
                            {syncing ? <FaSpinner className={styles.spinner} /> : <FaVideo />}
                            {' '}{syncing ? 'Syncing...' : 'Sync Recordings'}
                        </button>
                    )}
                </div>

                {statusFilter === 'past' && syncMessage && (
                    <p style={syncMessageStyle}>{syncMessage}</p>
                )}

                {selectMode && (
                    <div style={bulkBar}>
                        <label style={bulkSelectAllLabel}>
                            <input
                                type="checkbox"
                                checked={deletableMeetings.length > 0 && selectedKeys.length === deletableMeetings.length}
                                onChange={toggleSelectAll}
                            />
                            Select all ({deletableMeetings.length})
                        </label>
                        <button
                            style={bulkDeleteBtn}
                            disabled={selectedKeys.length === 0 || bulkDeleting}
                            onClick={handleBulkDelete}
                        >
                            {bulkDeleting ? <FaSpinner className={styles.spinner} /> : <FaTrash />}
                            {' '}Delete Selected ({selectedKeys.length})
                        </button>
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {loading && (
                        <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={styles.loader}>
                            <FaSpinner className={styles.spinner} />
                            <p>Loading meetings...</p>
                        </motion.div>
                    )}

                    {!loading && error && (
                        <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={styles.errorMessage}>
                            <p>Error: {error}</p>
                        </motion.div>
                    )}

                    {!loading && !error && meetings.length === 0 && (
                        <motion.div key="no-meetings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={styles.noMeetingsMessage}>
                            <p>No {statusFilter} meetings found.</p>
                        </motion.div>
                    )}

                    {!loading && !error && meetings.length > 0 && (
                        <motion.ul
                            key="meetings-list"
                            className={styles.meetingList}
                            initial="hidden"
                            animate="visible"
                            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
                        >
                            {meetings.map((meeting) => (
                                <motion.li
                                    key={meeting.meetingKey}
                                    className={styles.meetingItem}
                                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                >
                                    {selectMode && meeting.presenterZuid === CURRENT_USER_ZUID && statusFilter === 'upcoming' && (
                                        <input
                                            type="checkbox"
                                            style={itemCheckbox}
                                            checked={selectedKeys.includes(meeting.meetingKey)}
                                            onChange={() => toggleSelectKey(meeting.meetingKey)}
                                        />
                                    )}
                                    {/* Date & Time Section */}
                                    <div className={styles.meetingTimeInfo}>
                                        <FaSun />
                                        <span className={styles.boldness}>{meeting.sDate}</span>
                                        <span className={styles.boldness}>{meeting.sTime}</span>
                                        <FaGlobe />
                                        <span className={styles.boldness}>{meeting.durationInHours}</span>
                                    </div>

                                    {/* Topic Section */}
                                    <h3 className={styles.meetingTopic}>{meeting.topic}</h3>

                                    {/* Presenter Section */}
                                    <div className={styles.presenterInfo}>
                                        <img src={meeting.presenterAvatar} alt="Presenter Avatar" className={styles.presenterAvatar} />
                                        <div className={styles.presenterText}>
                                            <span className={styles.presenterName}>{meeting.presenterFullName}</span>
                                            <span className={styles.presenterDepartment}>My Department</span>
                                        </div>
                                    </div>

                                    {/* Action Buttons & Dropdown */}
                                    <div className={styles.buttonContainer}>
                                        {statusFilter === 'upcoming' && (
                                            <>
                                                {meeting.presenterZuid === CURRENT_USER_ZUID ? (
                                                    <button
                                                        onClick={() => handleStartMeeting(meeting)}
                                                        className={styles.startLink}
                                                        disabled={startingKey === meeting.meetingKey}
                                                    >
                                                        {startingKey === meeting.meetingKey
                                                            ? <FaSpinner className={styles.spinner} />
                                                            : <FaPlayCircle />}
                                                        {' '}Start
                                                    </button>
                                                ) : (
                                                    <a href={meeting.joinLink} target="_blank" rel="noopener noreferrer" className={styles.joinLink}>
                                                        <FaLink /> Join
                                                    </a>
                                                )}

                                                {meeting.presenterZuid === CURRENT_USER_ZUID && (
                                                    <div className={styles.dropdown}>
                                                        <button onClick={() => toggleDropdown(meeting.meetingKey)} className={styles.dropdownButton}>
                                                            <FaEllipsisV />
                                                        </button>
                                                        {openDropdown === meeting.meetingKey && (
                                                            <div className={styles.dropdownContent}>
                                                                 <button onClick={() => handleEdit(meeting.meetingKey)} className={styles.editButton}>
            <FaEdit /> Edit
        </button>
                                                                <button onClick={() => handleDelete(meeting.meetingKey)} className={styles.deleteButton}>
                                                                    <FaTrash /> Delete
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {statusFilter === 'past' && (
                                            <>
                                                {recordings[meeting.meetingKey]?.loading && (
                                                    <span style={recordingChecking}>
                                                        <FaSpinner className={styles.spinner} /> Checking recording...
                                                    </span>
                                                )}
                                                {recordings[meeting.meetingKey]?.hasRecording && (
                                                    <a
                                                        href={recordings[meeting.meetingKey].playUrl}
                                                        target="_blank" rel="noopener noreferrer"
                                                        style={recordingAvailableBtn}
                                                    >
                                                        <FaVideo /> Watch Recording
                                                    </a>
                                                )}
                                                {recordings[meeting.meetingKey] &&
                                                    !recordings[meeting.meetingKey].loading &&
                                                    !recordings[meeting.meetingKey].hasRecording && (
                                                        <span style={recordingUnavailable}>
                                                            <FaVideoSlash /> No recording
                                                        </span>
                                                )}
                                                <button
                                                    onClick={() => openRecordingModal(meeting)}
                                                    style={addRecordingBtn}
                                                >
                                                    <FaEdit /> {recordings[meeting.meetingKey]?.hasRecording ? 'Edit' : 'Add'} URL
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </motion.li>
                            ))}
                        </motion.ul>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>

        <AnimatePresence>
            {recordingModalMeeting && (
                <motion.div
                    style={modalOverlay}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeRecordingModal}
                >
                    <motion.div
                        style={modalCard}
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.96 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={modalHeader}>
                            <h3 style={modalTitle}>
                                <FaVideo /> Attach Recording
                            </h3>
                            <button onClick={closeRecordingModal} style={modalCloseBtn} aria-label="Close">
                                <FaTimes />
                            </button>
                        </div>

                        <p style={modalMeetingTopic}>{recordingModalMeeting.topic}</p>

                        <label style={modalLabel}>
                            Recording URL
                            <input
                                type="text"
                                autoFocus
                                placeholder="Paste the Zoho recording link here"
                                value={recordingUrlInput}
                                onChange={(e) => setRecordingUrlInput(e.target.value)}
                                style={modalInput}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRecording(); }}
                            />
                        </label>

                        {recordingModalError && <p style={modalErrorText}>{recordingModalError}</p>}

                        <div style={modalActions}>
                            <button onClick={closeRecordingModal} style={modalCancelBtn} disabled={savingRecording}>
                                Cancel
                            </button>
                            <button onClick={handleSaveRecording} style={modalSaveBtn} disabled={savingRecording}>
                                {savingRecording ? <FaSpinner className={styles.spinner} /> : <FaSave />}
                                {' '}Save
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
        </div>
    );
};

// ── Inline styles for bulk-select feature ─────────────────────────────────────
const selectToggleBtn = {
    marginLeft: 'auto', padding: '0.5rem 1rem', border: '1px solid #6a0dad',
    borderRadius: '6px', background: '#fff', color: '#6a0dad',
    fontWeight: '500', cursor: 'pointer',
};
const bulkBar = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#f8f0ff', border: '1px solid #d6b4f8', borderRadius: '10px',
    padding: '0.75rem 1rem', margin: '0.75rem 0',
};
const bulkSelectAllLabel = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    fontWeight: '500', color: '#4a5568', cursor: 'pointer',
};
const bulkDeleteBtn = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.5rem 1rem', border: 'none', borderRadius: '6px',
    background: '#e53e3e', color: '#fff', fontWeight: '600',
    cursor: 'pointer', opacity: 1,
};
const itemCheckbox = {
    width: '18px', height: '18px', marginRight: '0.5rem', cursor: 'pointer',
};
const recordingAvailableBtn = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.5rem 1rem', borderRadius: '8px', textDecoration: 'none',
    background: '#38a169', color: '#fff', fontWeight: '600', fontSize: '0.9rem',
};
const recordingUnavailable = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    color: '#a0aec0', fontSize: '0.85rem', fontStyle: 'italic',
};
const recordingChecking = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    color: '#718096', fontSize: '0.85rem',
};
const syncMessageStyle = {
    fontSize: '0.85rem', color: '#4a5568', margin: '0.5rem 0 0',
};
const addRecordingBtn = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid #cbd5e0',
    background: '#fff', color: '#4a5568', fontWeight: '600', fontSize: '0.82rem',
    cursor: 'pointer',
};
const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: '1rem',
};
const modalCard = {
    background: '#fff', borderRadius: '16px', padding: '1.75rem',
    width: '100%', maxWidth: '460px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
};
const modalHeader = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '0.5rem',
};
const modalTitle = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    fontSize: '1.15rem', fontWeight: '700', color: '#1a202c', margin: 0,
};
const modalCloseBtn = {
    border: 'none', background: 'transparent', color: '#a0aec0',
    fontSize: '1.1rem', cursor: 'pointer', padding: '0.25rem',
};
const modalMeetingTopic = {
    color: '#718096', fontSize: '0.9rem', margin: '0 0 1.25rem',
};
const modalLabel = {
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
    fontSize: '0.85rem', fontWeight: '600', color: '#4a5568',
};
const modalInput = {
    padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e0',
    fontSize: '0.9rem', fontWeight: '400', color: '#1a202c', outline: 'none',
};
const modalErrorText = {
    color: '#e53e3e', fontSize: '0.82rem', margin: '0.6rem 0 0',
};
const modalActions = {
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem',
};
const modalCancelBtn = {
    padding: '0.55rem 1.1rem', borderRadius: '8px', border: '1px solid #cbd5e0',
    background: '#fff', color: '#4a5568', fontWeight: '600', cursor: 'pointer',
};
const modalSaveBtn = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.55rem 1.2rem', borderRadius: '8px', border: 'none',
    background: '#38a169', color: '#fff', fontWeight: '700', cursor: 'pointer',
};

export default MeetingList;